import { useEffect, useState, useCallback } from "react";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Ticket, Loader2, Plus, Minus, Clock, X, History, FileText, Send } from "lucide-react";
import { toast } from "sonner";

/**
 * CarnetPanel — shows a customer's active support-hours package (carnet),
 * plus history of usages. Admins can create/close carnets. From within a
 * ticket, technicians can deduct hours (needs ticketId + customerId).
 *
 * Props:
 *   customerId (string, required): the customer whose carnet is displayed.
 *   ticketId   (string, optional): when present, shows the "Scarica ore" flow.
 *   userRole   (string, required): "admin" enables management; others read-only.
 *   onUsage    (function, optional): called after a successful hours deduction.
 */
export function CarnetPanel({ customerId, ticketId, userRole, onUsage }) {
  const isAdmin = userRole === "admin";
  const [carnet, setCarnet] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [useOpen, setUseOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [newHours, setNewHours] = useState("");
  const [newNote, setNewNote] = useState("");
  const [useHours, setUseHours] = useState("");
  const [useNote, setUseNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const currentMonth = new Date().toISOString().slice(0, 7);
  const [reportMonth, setReportMonth] = useState(currentMonth);
  const [sendingReport, setSendingReport] = useState(false);

  const downloadReport = () => {
    if (!carnet) return;
    window.open(`${API_BASE}/carnets/${carnet.id}/report-pdf?month=${reportMonth}`, "_blank", "noopener,noreferrer");
  };

  const sendReport = async () => {
    if (!carnet) return;
    setSendingReport(true);
    try {
      const { data } = await api.post(`/carnets/${carnet.id}/send-report?month=${reportMonth}`);
      toast.success(`Report inviato a ${data.to} (${data.usages} interventi, ${data.total_hours}h)`);
      setReportOpen(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSendingReport(false); }
  };

  const load = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const [c, h] = await Promise.all([
        api.get(`/customers/${customerId}/carnet`).catch(() => ({ data: null })),
        api.get(`/customers/${customerId}/carnet/history`).catch(() => ({ data: [] })),
      ]);
      setCarnet(c?.data);
      setHistory(h?.data || []);
    } finally { setLoading(false); }
  }, [customerId]);

  useEffect(() => { load(); }, [load]);

  const createCarnet = async () => {
    const hrs = parseFloat(newHours);
    if (!hrs || hrs <= 0) { toast.error("Ore non valide"); return; }
    setSaving(true);
    try {
      await api.post(`/customers/${customerId}/carnet`, { hours_total: hrs, note: newNote });
      toast.success(`Carnet ${hrs}h creato`);
      setCreateOpen(false); setNewHours(""); setNewNote("");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const closeCarnet = async () => {
    if (!window.confirm("Chiudere il carnet attivo? Non potrai più scaricare ore da questo carnet.")) return;
    setSaving(true);
    try {
      await api.post(`/carnets/${carnet.id}/close`);
      toast.success("Carnet chiuso");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const deductHours = async () => {
    const hrs = parseFloat(useHours);
    if (!hrs || hrs <= 0) { toast.error("Ore non valide"); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/tickets/${ticketId}/carnet-use`, { hours_used: hrs, note: useNote });
      toast.success(`Scaricate ${hrs}h · residuo ${data.carnet_hours_remaining}h`);
      setUseOpen(false); setUseHours(""); setUseNote("");
      await load();
      onUsage?.(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (loading && !carnet) return <div className="text-xs text-zinc-500">Caricamento carnet...</div>;

  const percentUsed = carnet ? Math.round(((carnet.hours_total - carnet.hours_remaining) / carnet.hours_total) * 100) : 0;

  return (
    <>
      <Card className="rounded-sm border-zinc-200 bg-white p-5" data-testid="carnet-panel">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <Ticket className="h-4 w-4 text-brand" />
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Carnet ore prepagate</div>
          </div>
          <button data-testid="carnet-history-btn" onClick={() => setHistoryOpen(true)} className="text-[11px] text-zinc-500 hover:text-brand flex items-center gap-1">
            <History className="h-3 w-3" /> Storia
          </button>
        </div>
        {carnet ? (
          <div>
            <div className="flex items-baseline gap-2">
              <span data-testid="carnet-remaining" className="font-display text-3xl font-bold text-zinc-900">{carnet.hours_remaining}</span>
              <span className="text-sm text-zinc-500">/ {carnet.hours_total}h</span>
              <Badge className="ml-auto rounded-sm text-[10px] uppercase tracking-widest bg-emerald-50 text-emerald-800 border border-emerald-200">Attivo</Badge>
            </div>
            <div className="mt-3 h-1.5 rounded-full bg-zinc-100 overflow-hidden">
              <div className={`h-full transition-all ${percentUsed > 80 ? "bg-red-500" : percentUsed > 50 ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${percentUsed}%` }} />
            </div>
            {carnet.note && <div className="mt-2 text-xs text-zinc-600 italic">{carnet.note}</div>}
            <div className="mt-4 flex flex-wrap gap-2">
              {ticketId && carnet.hours_remaining > 0 && (
                <Button data-testid="carnet-use-btn" size="sm" onClick={() => setUseOpen(true)} className="bg-brand hover:bg-brand-dark text-white rounded-sm text-xs">
                  <Minus className="h-3.5 w-3.5 mr-1" /> Scarica ore
                </Button>
              )}
              {isAdmin && (
                <Button data-testid="carnet-report-btn" size="sm" onClick={() => setReportOpen(true)} variant="outline" className="rounded-sm text-xs">
                  <FileText className="h-3.5 w-3.5 mr-1" /> Report mensile
                </Button>
              )}
              {isAdmin && (
                <Button data-testid="carnet-close-btn" size="sm" onClick={closeCarnet} variant="outline" className="rounded-sm text-xs">
                  <X className="h-3.5 w-3.5 mr-1" /> Chiudi carnet
                </Button>
              )}
            </div>
          </div>
        ) : (
          <div>
            <div className="text-sm text-zinc-500 mb-3">Nessun carnet attivo per questo cliente.</div>
            {isAdmin && (
              <Button data-testid="carnet-create-btn" size="sm" onClick={() => setCreateOpen(true)} className="bg-brand hover:bg-brand-dark text-white rounded-sm text-xs">
                <Plus className="h-3.5 w-3.5 mr-1" /> Nuovo carnet
              </Button>
            )}
          </div>
        )}
      </Card>

      {/* Create carnet */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="rounded-sm max-w-md" data-testid="carnet-create-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Nuovo carnet</DialogTitle>
            <DialogDescription className="text-xs text-zinc-500">Crea un pacchetto di ore prepagate per questo cliente.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Ore totali</Label>
              <Input data-testid="carnet-new-hours" type="number" step="0.5" min="0.5" value={newHours} onChange={(e) => setNewHours(e.target.value)} placeholder="10" className="mt-1 rounded-sm" autoFocus />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Nota (opzionale)</Label>
              <Input data-testid="carnet-new-note" value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="Es. Fattura #123, acquistato 10/02/2026" className="mt-1 rounded-sm" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} className="rounded-sm">Annulla</Button>
            <Button data-testid="carnet-new-submit" onClick={createCarnet} disabled={saving} className="bg-brand hover:bg-brand-dark text-white rounded-sm">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Crea carnet"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deduct hours */}
      {ticketId && (
        <Dialog open={useOpen} onOpenChange={setUseOpen}>
          <DialogContent className="rounded-sm max-w-md" data-testid="carnet-use-dialog">
            <DialogHeader>
              <DialogTitle className="font-display tracking-tight">Scarica ore da carnet</DialogTitle>
              <DialogDescription className="text-xs text-zinc-500">
                {carnet && <>Residuo attuale: <b>{carnet.hours_remaining}h</b> di {carnet.hours_total}h totali</>}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-500">Ore da scaricare</Label>
                <Input data-testid="carnet-use-hours" type="number" step="0.25" min="0.25" max={carnet?.hours_remaining || 0} value={useHours} onChange={(e) => setUseHours(e.target.value)} placeholder="1.5" className="mt-1 rounded-sm" autoFocus />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-500">Nota intervento</Label>
                <Input data-testid="carnet-use-note" value={useNote} onChange={(e) => setUseNote(e.target.value)} placeholder="Es. Sostituzione tastiera + collaudo" className="mt-1 rounded-sm" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setUseOpen(false)} className="rounded-sm">Annulla</Button>
              <Button data-testid="carnet-use-submit" onClick={deductHours} disabled={saving} className="bg-brand hover:bg-brand-dark text-white rounded-sm">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Clock className="h-4 w-4 mr-2" /> Scarica</>}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* History */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="rounded-sm max-w-2xl" data-testid="carnet-history-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Storia carnet</DialogTitle>
            <DialogDescription className="text-xs text-zinc-500">Tutti i carnet e utilizzi di questo cliente.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto space-y-4 pr-1">
            {history.length === 0 && <div className="text-sm text-zinc-500">Nessun carnet registrato.</div>}
            {history.map((c) => (
              <div key={c.id} className="border border-zinc-200 rounded-sm p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm">
                    <b>{c.hours_total}h</b> totali · residue <b>{c.hours_remaining}h</b>
                  </div>
                  <Badge className={`rounded-sm text-[10px] uppercase tracking-widest border ${c.active ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-zinc-100 text-zinc-600 border-zinc-200"}`}>{c.active ? "Attivo" : "Chiuso"}</Badge>
                </div>
                <div className="text-[11px] text-zinc-500">
                  Creato {new Date(c.created_at).toLocaleDateString("it-IT")} da {c.created_by_name}
                  {c.note && <> · <i>{c.note}</i></>}
                </div>
                {c.usages && c.usages.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-zinc-100 space-y-1">
                    {c.usages.map((u) => (
                      <div key={u.id} className="text-xs text-zinc-700 flex justify-between gap-2">
                        <span>
                          <b>-{u.hours_used}h</b> · Ticket {u.ticket_number} · {u.used_by_name}
                          {u.note && <> — {u.note}</>}
                        </span>
                        <span className="text-zinc-400">{new Date(u.used_at).toLocaleDateString("it-IT")}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
      {/* Monthly Report */}
      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent className="rounded-sm max-w-md" data-testid="carnet-report-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Report mensile Carnet</DialogTitle>
            <DialogDescription className="text-xs text-zinc-500">
              Genera un PDF con tutti gli utilizzi del carnet nel mese selezionato. Puoi scaricarlo o inviarlo direttamente via email al cliente.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Mese di riferimento</Label>
              <Input data-testid="carnet-report-month" type="month" value={reportMonth} onChange={(e) => setReportMonth(e.target.value)} className="mt-1 rounded-sm" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={downloadReport} data-testid="carnet-report-download" className="rounded-sm">
              <FileText className="h-4 w-4 mr-2" /> Scarica PDF
            </Button>
            <Button data-testid="carnet-report-send" onClick={sendReport} disabled={sendingReport} className="bg-brand hover:bg-brand-dark text-white rounded-sm">
              {sendingReport ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-2" /> Invia al cliente</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
